import { useAuth0 } from '@auth0/auth0-react';
import { useQuery } from '@tanstack/react-query';
import { useLocation } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { V2SupportService } from 'src/api/generated';
import { useCurrentUser } from 'src/modules/shared/hooks/api/admin/users';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { SupportMessenger } from '../utils/support-messenger';
import type { SupportState } from '../types/support.types';

/** Coordinates authenticated session fetching, refresh and the SDK lifecycle. */
export function useSupportSession() {
  const { isAuthenticated } = useAuth0();
  const { user } = useCurrentUser(isAuthenticated);
  const { language } = useI18n();
  const pathname = useLocation({ select: (location) => location.pathname });
  const identity = isAuthenticated && user ? `${user.id}:${user.tenantId}` : '';
  const [stopped, setStopped] = useState(false);
  const [snapshot, setSnapshot] = useState<SupportState & { identity: string }>(
    {
      identity: '',
      status: 'loading',
      unreadCount: 0,
      isOpen: false,
    }
  );
  const client = useMemo(
    () => new SupportMessenger((state) => setSnapshot({ ...state, identity })),
    [identity]
  );
  const session = useQuery({
    queryKey: ['support/messenger-session', identity],
    queryFn: ({ signal }) => {
      const request = V2SupportService.getMessengerSession();
      const abort = () => request.cancel();
      signal.addEventListener('abort', abort, { once: true });
      return request.finally(() => signal.removeEventListener('abort', abort));
    },
    enabled: Boolean(identity) && !stopped,
    staleTime: 45 * 60 * 1000,
    refetchInterval: 45 * 60 * 1000,
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: true,
    gcTime: 0,
    retry: 1,
  });

  useEffect(() => () => client.stop(), [client]);
  useEffect(() => {
    if (!identity || stopped) {
      client.stop();
      return;
    }
    if (session.data?.session) client.connect(session.data.session, language);
    else if (session.data || session.isError) client.stop();
  }, [
    client,
    identity,
    stopped,
    session.data,
    session.dataUpdatedAt,
    session.isError,
    language,
  ]);
  useEffect(() => {
    client.routeChanged();
  }, [client, pathname]);

  const state: SupportState =
    snapshot.identity === identity
      ? snapshot
      : { status: 'loading', unreadCount: 0, isOpen: false };

  return {
    ...state,
    user,
    open: () => client.open(),
    shutdown: () => {
      setStopped(true);
      client.stop();
    },
    retry: () => window.location.reload(),
    loading: session.isFetching || state.status === 'loading',
  };
}
